import { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { User, Shield, Settings, Phone, Mail, MapPin, CreditCard, ChevronRight, Check } from "lucide-react";

export default function BetboomPreview() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const initialTab = urlSearchParams.get("tab") || "personal";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [data, setData] = useState(null);
  const [showBrowser, setShowBrowser] = useState(true);
  const [browserTheme, setBrowserTheme] = useState('dark'); // 'dark' or 'light'
  
  // Profile data state
  const [profileData, setProfileData] = useState({
    firstName: "Иван",
    lastName: "Иванов",
    gender: "male",
    phone: "+7 (999) 123-45-67",
    email: "ivan@example.com",
    city: "Москва",
    verified: true
  });

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const browserParam = searchParams.get('showBrowser');
    
    if (browserParam !== null) {
      setShowBrowser(browserParam !== 'false');
    }
    
    if (dataParam) {
      try {
        const parsed = JSON.parse(decodeURIComponent(dataParam));
        setData(parsed);
        // Check showBrowser in data
        if (parsed.showBrowser !== undefined) {
          setShowBrowser(parsed.showBrowser);
        }
        // Check browserTheme in data
        if (parsed.browserTheme) {
          setBrowserTheme(parsed.browserTheme);
        }
        // Update profile data from parsed data
        setProfileData({
          firstName: parsed.firstName || "Иван",
          lastName: parsed.lastName || "Иванов",
          gender: parsed.gender === "Мужской" ? "male" : parsed.gender === "Женский" ? "female" : "male",
          phone: parsed.phone || "+7 (999) 123-45-67",
          email: parsed.email || "ivan@example.com",
          city: parsed.city || "Москва",
          verified: true
        });
      } catch (e) {
        console.error("Error parsing data:", e);
      }
    }
  }, [searchParams]);

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const tabs = [
    { id: "personal", label: "Личные данные", icon: User },
    { id: "security", label: "Безопасность", icon: Shield },
    { id: "settings", label: "Настройки", icon: Settings },
  ];

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center">
        <div className="text-white">Загрузка...</div>
      </div>
    );
  }

  const userId = data.betboomId || Math.floor(100000 + Math.random() * 900000);
  // Use template_balance from OZON profile
  const balance = data.balance || 0;

  // Browser Frame Component - supports light and dark themes
  const BrowserFrame = ({ theme = 'dark' }) => {
    const isLight = theme === 'light';
    const bgColor = isLight ? '#dee1e6' : '#202124';
    const tabBgActive = isLight ? '#ffffff' : '#0f0f10';
    const tabBgInactive = isLight ? '#c8ccd1' : '#35363a';
    const textActive = isLight ? '#202124' : '#e8eaed';
    const textInactive = isLight ? '#5f6368' : '#9aa0a6';
    const toolbarBg = isLight ? '#f1f3f4' : '#292a2d';
    const urlBarBg = isLight ? '#ffffff' : '#202124';
    const urlBarBorder = isLight ? '#dadce0' : '#3c4043';
    
    return (
      <div className="flex-shrink-0" style={{ backgroundColor: bgColor }}>
        <div className="flex items-center h-[38px]" style={{ backgroundColor: bgColor }}>
          <div className="flex items-end flex-1 pl-2">
            {/* Tab 1 - Ozon Карта */}
            <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] cursor-pointer" style={{ backgroundColor: tabBgInactive }}>
              <img src="https://finance.ozon.ru/favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="text-[13px] truncate flex-1" style={{ color: textInactive }}>Ozon Карта</span>
            </div>
            {/* Tab 2 - BetBoom (ACTIVE) */}
            <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] cursor-pointer" style={{ backgroundColor: tabBgActive }}>
              <img src="https://betboom.ru/favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
              <span className="text-[13px] truncate flex-1" style={{ color: textActive }}>BetBoom</span>
            </div>
            <button className="w-7 h-7 flex items-center justify-center rounded-full ml-1 mb-1" style={{ color: textInactive }}>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
            </button>
          </div>
          <div className="flex items-center h-full">
            <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: textInactive }}><svg className="w-[10px] h-[1px]" viewBox="0 0 10 1"><rect fill="currentColor" width="10" height="1"/></svg></button>
            <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: textInactive }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1"><rect x="0.5" y="0.5" width="9" height="9"/></svg></button>
            <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#e81123] hover:text-white" style={{ color: textInactive }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.2"><line x1="0" y1="0" x2="10" y2="10"/><line x1="10" y1="0" x2="0" y2="10"/></svg></button>
          </div>
        </div>
        <div className="flex items-center h-[40px] px-2" style={{ backgroundColor: toolbarBg }}>
          <div className="flex items-center gap-1 mr-2">
            <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textInactive }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg></button>
            <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: isLight ? '#c0c4c8' : '#5f6368' }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg></button>
            <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textInactive }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg></button>
          </div>
          <div className="flex-1 flex items-center h-[32px] px-4 rounded-full" style={{ backgroundColor: urlBarBg, border: `1px solid ${urlBarBorder}` }}>
            <svg className="w-4 h-4 mr-3 flex-shrink-0" style={{ color: textInactive }} viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
            <span className="text-[14px]" style={{ color: textActive }}>betboom.ru/personal</span>
          </div>
          <div className="flex items-center gap-1 ml-3">
            <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textInactive }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></button>
            <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textInactive }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="2"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/></svg></button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Global styles to ensure dark background fills entire page */}
      <style>{`
        html, body, #root {
          background-color: #0f0f10 !important;
          min-height: 100vh;
          margin: 0;
          padding: 0;
        }
      `}</style>
      <div className="flex flex-col template-preview-scale" style={{ backgroundColor: '#0f0f10', minHeight: '100vh', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        {showBrowser && <BrowserFrame theme={browserTheme} />}
        
        <div className="flex-1 bg-[#0f0f10] text-white" style={{ minHeight: showBrowser ? 'auto' : '100vh' }}>
      {/* Header */}
      <header className="bg-[#0d0d0d] border-b border-gray-800">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Nav */}
            <div className="flex items-center gap-8">
              <span className="text-white font-bold text-xl tracking-tight">BetBoom</span>
              <nav className="hidden md:flex items-center gap-6">
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Линия</a>
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Киберспорт</a>
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Спорт 24/7</a>
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Акции</a>
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Клубы</a>
              </nav>
            </div>
            
            {/* Balance & Profile */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-[#f9d547] font-bold text-lg">{balance.toLocaleString()}₽</span>
                <span className="text-gray-500 text-xs">Игра на деньги</span>
              </div>
              <div className="flex items-center gap-3">
                <button className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </button>
                <div className="w-9 h-9 bg-[#1a1a1a] rounded-full flex items-center justify-center border border-gray-700">
                  <User className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <div className="max-w-[1200px] mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Профиль</h1>
          <p className="text-gray-400">Управление личными данными и настройками аккаунта</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-[#1a1a1a] rounded-lg border border-gray-800 overflow-hidden">
              {/* User Card */}
              <div className="p-6 border-b border-gray-800">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#f9d547] to-[#f5a623] flex items-center justify-center">
                    <User className="w-8 h-8 text-black" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">{profileData.firstName} {profileData.lastName}</div>
                    <div className="text-gray-400 text-sm">ID: {userId}</div>
                    {profileData.verified && (
                      <div className="flex items-center gap-1 text-green-500 text-xs mt-1">
                        <Check className="w-3 h-3" />
                        Верифицирован
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <nav className="p-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        activeTab === tab.id 
                          ? "bg-[#f9d547] text-black" 
                          : "text-gray-400 hover:bg-gray-800 hover:text-white"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Personal Data Tab */}
            {activeTab === "personal" && (
              <div className="space-y-6">
                {/* Personal Info Card */}
                <div className="bg-[#1a1a1a] rounded-lg border border-gray-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold">Личные данные</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Фамилия</label>
                      <div className="text-white text-lg">{profileData.lastName}</div>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Имя</label>
                      <div className="text-white text-lg">{profileData.firstName}</div>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Пол</label>
                      <div className="text-white text-lg">
                        {profileData.gender === "male" ? "Мужской" : "Женский"}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Город</label>
                      <div className="text-white text-lg flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        {profileData.city}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Info Card */}
                <div className="bg-[#1a1a1a] rounded-lg border border-gray-800 p-6">
                  <h2 className="text-xl font-semibold mb-6">Контактная информация</h2>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-[#0f0f10] rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <div className="text-gray-400 text-sm">Телефон</div>
                          <div className="text-white">{profileData.phone}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-green-500 text-sm">
                        <Check className="w-4 h-4" />
                        Подтверждён
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-[#0f0f10] rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <Mail className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <div className="text-gray-400 text-sm">Email</div>
                          <div className="text-white">{profileData.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-green-500 text-sm">
                        <Check className="w-4 h-4" />
                        Подтверждён
                      </div>
                    </div>
                  </div>
                </div>

                {/* Verification Status Card */}
                <div className="bg-[#1a1a1a] rounded-lg border border-gray-800 p-6">
                  <h2 className="text-xl font-semibold mb-6">Статус верификации</h2>
                  
                  <div className="flex items-center gap-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-green-500 font-semibold">Аккаунт верифицирован</div>
                      <div className="text-gray-400 text-sm">Все данные проверены и подтверждены</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === "security" && (
              <div className="space-y-6">
                <div className="bg-[#1a1a1a] rounded-lg border border-gray-800 p-6">
                  <h2 className="text-xl font-semibold mb-6">Безопасность аккаунта</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-[#0f0f10] rounded-lg hover:bg-[#151515] transition-colors cursor-pointer">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#f9d547]/20 flex items-center justify-center">
                          <Shield className="w-5 h-5 text-[#f9d547]" />
                        </div>
                        <div>
                          <div className="text-white font-medium">Изменить пароль</div>
                          <div className="text-gray-400 text-sm">Последнее изменение: 30 дней назад</div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-[#0f0f10] rounded-lg hover:bg-[#151515] transition-colors cursor-pointer">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <div className="text-white font-medium">Двухфакторная аутентификация</div>
                          <div className="text-green-500 text-sm">Включена</div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-[#0f0f10] rounded-lg hover:bg-[#151515] transition-colors cursor-pointer">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <div className="text-white font-medium">Привязанные карты</div>
                          <div className="text-gray-400 text-sm">2 карты привязано</div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === "settings" && (
              <div className="space-y-6">
                <div className="bg-[#1a1a1a] rounded-lg border border-gray-800 p-6">
                  <h2 className="text-xl font-semibold mb-6">Настройки уведомлений</h2>
                  
                  <div className="space-y-4">
                    {[
                      { label: "Push-уведомления", description: "Уведомления о ставках и акциях", enabled: true },
                      { label: "Email-рассылка", description: "Новости и специальные предложения", enabled: true },
                      { label: "SMS-уведомления", description: "Важные события по аккаунту", enabled: false },
                    ].map((setting, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-[#0f0f10] rounded-lg">
                        <div>
                          <div className="text-white font-medium">{setting.label}</div>
                          <div className="text-gray-400 text-sm">{setting.description}</div>
                        </div>
                        <button className={`w-12 h-6 rounded-full transition-colors ${setting.enabled ? "bg-[#f9d547]" : "bg-gray-600"}`}>
                          <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${setting.enabled ? "translate-x-6" : "translate-x-0.5"}`}></div>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-[#1a1a1a] rounded-lg border border-gray-800 p-6">
                  <h2 className="text-xl font-semibold mb-6">Язык и регион</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Язык интерфейса</label>
                      <select className="w-full bg-[#0f0f10] border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#f9d547]">
                        <option>Русский</option>
                        <option>English</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Часовой пояс</label>
                      <select className="w-full bg-[#0f0f10] border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#f9d547]">
                        <option>Москва (UTC+3)</option>
                        <option>Екатеринбург (UTC+5)</option>
                        <option>Владивосток (UTC+10)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
    </>
  );
}
